import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { getCurrentQuarter, getCurrentYear, getPeriodQuarterLabel, Period, PeriodQuarter, PeriodQuarterValues, PeriodTypeQuarter, PeriodYear, PeriodYearValues } from "@dto/period";
import { TW_ChoiceScrollArea, TW_CommonCommandInput } from "@utils/twClasses";
import { Square, SquareCheck } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PeriodEditionFormProps } from "./PeriodCell";

export const PeriodQuarterAndYearEditionForm = memo(function PeriodQuarterAndYearEditionForm({ field, value, updatePeriod, searchValue, setSearchValue, searchInputRef, displaySearch = true, hasValidation = true, disabled = false }: PeriodEditionFormProps) {

    const { t } = useTranslation();
    const [quarterValue, setQuarterValue] = useState(value?.quarter() ?? getCurrentQuarter());
    const [yearValue, setYearValue] = useState(value?.year() ?? getCurrentYear());
    const [modified, setModified] = useState(value === undefined);

    // useEffect(() => {
    //     if (!hasValidation && !disabled && quarterValue !== value?.quarter() && yearValue !== value?.year()) {
    //         validate(quarterValue, yearValue);
    //     }
    // }, []);

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (!setSearchValue) {
            return;
        }
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                setSearchValue("");
                break;
            case "Enter":
                if (searchValue === "") {
                    event.preventDefault();
                    event.stopPropagation();
                    validate(quarterValue, yearValue);
                } else {
                    event.stopPropagation();
                    setSearchValue("");
                }
                break;
            case "Tab":
                event.preventDefault();
                validate(quarterValue, yearValue);
                setSearchValue("");
                break;
        }
    }

    const onQuarterChange = (quarterValue: string) => {
        changeQuarter(parseInt(quarterValue) as PeriodQuarter);
    }

    const onQuarterLabelChange = (quarterLabel: string) => {
        const quarter = Period.quarterValue(quarterLabel, t);
        changeQuarter(quarter);
    }

    function changeQuarter(quarter: PeriodQuarter) {
        if (quarter === quarterValue) {
            return;
        }
        setQuarterValue(quarter);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(quarter, yearValue);
        }
    }

    const onYearChange = (yearName: string) => {
        if (yearName === yearValue.toString()) {
            return;
        }
        const year = parseInt(yearName);
        setYearValue(year);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(quarterValue, year);
        }
    }

    function validate(quarter: number | undefined, year: number | undefined) {
        if (disabled) {
            return;
        }
        if (quarter === value?.quarter() && year === value?.year()) {
            return;
        }
        if (!quarter || !year) {
            updatePeriod(undefined);
        } else {
            const monthValue = (quarter - 1) * 3 + 1;
            const newPeriod = Period.newPeriod(year, monthValue as PeriodQuarter, 1, PeriodTypeQuarter);
            updatePeriod(newPeriod);
        }
        setModified(false);
    }

    const hasSearch = displaySearch && setSearchValue;

    const onValidateClick = () => {
        validate(quarterValue, yearValue);
    }

    return (
        <div key={"filter-quarter-" + field?._key} className={"flex flex-col sm:flex-row flex-start items-stretch sm:items-start gap-2 " + (hasSearch ? " w-full " : " w-48 ")}>
            {hasSearch &&
                <div className="flex flex-col flex-start items-center gap-1 grow">
                    <Command onKeyDown={onKeyDown} className="min-w-0">
                        <CommandInput
                            placeholder={t("data.search.placeholder")}
                            className={TW_CommonCommandInput + " h-10 min-h-10"}
                            autoFocus={true}
                            value={searchValue}
                            onValueChange={(value) => setSearchValue(value)}
                            ref={searchInputRef}
                            disabled={disabled}
                        />
                        {searchValue === "" &&
                            <Select onValueChange={onQuarterChange} value={quarterValue.toString()} disabled={disabled}>
                                <SelectTrigger className="w-full grow mt-1 outline-none focus:outline-none">
                                    <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {PeriodQuarterValues.map((quarter: PeriodQuarter) => {
                                        const quarterLabel = getPeriodQuarterLabel(quarter, t);
                                        const currQuarterValue = quarter.toString();
                                        return (
                                            <SelectItem key={currQuarterValue} value={currQuarterValue} onSelect={() => onQuarterChange(currQuarterValue)}>{quarterLabel}</SelectItem>
                                        )
                                    })}
                                </SelectContent>
                            </Select>
                        }
                        {searchValue !== "" &&
                            <div className={TW_ChoiceScrollArea}>
                                <CommandList className="h-full max-h-full w-full">
                                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                                    <CommandGroup>
                                        {PeriodQuarterValues.map((quarter: PeriodQuarter) => {
                                            const quarterLabel = getPeriodQuarterLabel(quarter, t);
                                            return (
                                                <CommandItem key={quarter} value={quarterLabel} onSelect={() => onQuarterLabelChange(quarterLabel)}>{quarterLabel}</CommandItem>
                                            )
                                        })}
                                    </CommandGroup>
                                </CommandList>
                            </div>
                        }
                    </Command>
                </div>
            }
            {!hasSearch &&
                <Select onValueChange={onQuarterChange} value={quarterValue.toString()} disabled={disabled}>
                    <SelectTrigger className={"h-9 grow outline-none focus:outline-none" + (hasSearch ? " w-24 min-w-24 " : " w-full")}>
                        <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                        {PeriodQuarterValues.map((quarter: PeriodQuarter) => {
                            const quarterLabel = getPeriodQuarterLabel(quarter, t);
                            const currQuarterValue = quarter.toString();
                            return (
                                <SelectItem key={currQuarterValue} value={currQuarterValue} onSelect={() => onQuarterChange(currQuarterValue)}>{quarterLabel}</SelectItem>
                            )
                        })}
                    </SelectContent>
                </Select>
            }
            <div className="flex flex-col flex-start items-center gap-0 grow">
                {hasSearch &&
                    <div className="hidden sm:block w-full h-10 min-h-10 border-none border-transparent"></div>
                }
                <div className={"flex flex-row flex-start items-center gap-1" + (hasSearch ? " mt-1" : "")}>
                    <Select onValueChange={onYearChange} value={yearValue.toString()} disabled={disabled}>
                        <SelectTrigger>
                            <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                        </SelectTrigger>
                        <SelectContent>
                            {PeriodYearValues.map((year: PeriodYear) => {
                                const currYearValue = year.toString();
                                return (
                                    <SelectItem key={currYearValue} value={currYearValue} onSelect={() => onYearChange(currYearValue)}>{currYearValue}</SelectItem>
                                )
                            })}
                        </SelectContent>
                    </Select>
                    {hasValidation && modified && <Square onClick={onValidateClick} className="w-6 h-6 min-w-6 min-h-6 text-gray-500 cursor-pointer" />}
                    {hasValidation && !modified && <SquareCheck className="w-6 h-6 min-w-6 min-h-6 text-green-500 cursor-default" />}
                </div>
            </div>
        </div>
    )
}, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.value === nextProps.value && prevProps.searchValue === nextProps.searchValue && prevProps.searchInputRef === nextProps.searchInputRef && prevProps.disabled === nextProps.disabled;
});